import React, {useEffect, useRef} from 'react';
import {TextDropdown} from 'design-system/compatibility/LegacyTextDropdown';
import {guessMinutes, loadScript} from 'Util/Utilities';
import '../Common.css';
import {UNDEF} from '../Constants';
import {minsToAMPM, minsToMilitary} from '../Util/DateUtils';
import './CommonComponents.css';

export {default as LoadingSpinner} from 'design-system/compatibility/LegacyLoadingSpinner';
export {default as Snackbar} from 'design-system/compatibility/LegacySnackbar';
export {default as PortalDialog} from 'design-system/compatibility/LegacyPortalDialog';
export {default as OkCancelDialog} from 'design-system/compatibility/LegacyOkCancelDialog';

type PureCompProps = {
  watchList?: any[];
  children?: React.ReactNode;
};
type PureCompState = {};

export class PureComp extends React.Component<PureCompProps, PureCompState> {
  shouldComponentUpdate(nextProps: PureCompProps, _nextState: PureCompState) {
    const self = this,
      props = self.props;
    const oldWatchList = props.watchList || [],
      newWatchList = nextProps.watchList || [];
    for (let k = oldWatchList.length; k--; ) {
      if (oldWatchList[k] !== newWatchList[k]) return true;
    }
    return false;
  }

  render() {
    return this.props.children;
  }
}

// Time display: am/pm format
// Input/Output: Military time format: '14:00'
export function TimePicker(props: any) {
  const list = props.timeEntries || [];
  const ampmOutput = props.ampmOutput;

  const guessTime = (value: string) => {
    const minutes = guessMinutes(value);
    if (minutes === UNDEF) return;
    const entry = list.find((v: any) => v.mins === minutes);
    return entry || {mins: minutes, name: minsToAMPM(minutes, false)};
  };

  const toMilitary = (mins: number) => {
    return mins !== UNDEF && minsToMilitary(mins);
  };
  const minsFromMilitaryTime = (time: string) => {
    if (!time) return;
    if (ampmOutput) return time;

    const arr = time.split(':');
    const minutes = parseInt(arr[0]) * 60 + parseInt(arr[1]);
    //const entry = list.find((v:any) => v.mins === minutes);
    //return entry || {mins: minutes, name: minsToAMPM(minutes, false)};
    return minsToAMPM(minutes, false);
  };

  return (
    <TextDropdown
      className={props.className}
      style={props.style}
      width={props.width}
      lineHeight={props.lineHeight}
      inputClassName={props.inputClassName}
      popupMaxHeight="200px"
      placeholder={props.placeholder}
      popupDisabled={props.popupDisabled}
      readOnly={props.readOnly}
      hideFocusBar={props.hideFocusBar}
      dontDisableDocScroll={props.dontDisableDocScroll}
      titleProperty="name"
      idProperty="mins"
      showDropdownArrow={props.showDropdownArrow}
      matchFn={guessTime}
      items={list}
      selectedItem={minsFromMilitaryTime(props.selectedItem)}
      onChange={(_: any, item: any) => props.onChange(!item ? UNDEF : ampmOutput ? item.name : toMilitary(item.mins))}
    />
  );
}

type GoogleAutocompleteProps = {
  inputRef: any;
  children?: any;
  onPlaceChange: (place: any) => void;
};
export const GoogleAutocomplete: React.FC<GoogleAutocompleteProps> = ({children, inputRef, onPlaceChange}) => {
  const autoComplete = useRef(null);
  const loadingState = useRef(false);

  useEffect(() => {
    const field = inputRef.current;
    const listener = 'focus';
    const win: any = window;

    if (!field) return;

    function onScriptLoad(inputField: HTMLInputElement) {
      if (autoComplete.current) return;
      const autoComp = new win.google.maps.places.Autocomplete(inputField, {
        componentRestrictions: {
          country: [(process.env.REACT_APP_REGION || '').replace('_LOCAL', '')],
        },
      });

      autoComplete.current = autoComp;

      autoComp.setFields(['address_components', 'name']);
      autoComp.addListener('place_changed', function () {
        const place = autoComp.getPlace();
        const adr = place.address_components;
        adr && onPlaceChange(adr);
      });
    }

    function onFocus() {
      if (!loadingState.current) {
        loadingState.current = true;
        if (!win.google) {
          loadScript(
            `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GEOCODING_KEY}&libraries=places&callback=Function.prototype`,
            () => onScriptLoad(field),
          );
        } else {
          onScriptLoad(field);
        }
      }
    }

    field.addEventListener(listener, onFocus);

    return function cleanup() {
      field.removeEventListener(listener, onFocus);
    };
  });

  return children;
};
